import axios, { AxiosError, AxiosPromise } from "axios";
import Cookie from "js-cookie";
import { REST_UTILS_URL, COOKIE_LANG } from "customGlobal";
import auth, { updateToken } from "auth";
import { getUIContext } from "./common";

const apiUtils = axios.create({
  baseURL: REST_UTILS_URL
});

apiUtils.interceptors.request.use(
  async config => {
    await updateToken();
    config.headers.Authorization = `Bearer ${auth.token}`;
    config.headers["Accept-Language"] = Cookie.get(COOKIE_LANG);
    return config;
  },
  (error: AxiosError<any>) => {
    return Promise.reject(error);
  }
);

apiUtils.interceptors.response.use(
  res => res,
  (error: AxiosError<any>) => {
    if (error.response && error.response.status === 401) {
      // redirectToAuth();
      return;
    }

    return Promise.reject(error);
  }
);

export interface FindViewParams {
  contextKey?: string;
  tableName: string;
  filter?: string;
  first?: number;
  size?: number | false;
  groupBy?: string[];
  includes?: string[];
}

interface FindAllParams {
  contextKey?: string;
  tableName: string;
  filter?: string;
  first?: number;
  size?: number | false;
  includeJoinParent?: boolean;
  includeOels?: string[];
  includeStyle?: boolean;
  labelContext?: string;
  labelDetailsContext?: string;
}

interface PagedResource<T> {
  data: T[];
  meta: {
    totalRecords: number;
    size: number;
  };
}

export function findView<T = Record<string, any>>(
  params: FindViewParams
): AxiosPromise<PagedResource<T>> {
  const allParamsStr = configureUrlParamsForSearch(params);

  let groupByStr = "";
  if (params.groupBy) {
    groupByStr = "&groupBy=" + params.groupBy.join(",");
  }

  let includesStr = "";
  if (params.includes) {
    includesStr = "&includes=" + params.includes.join(",");
  }

  return apiUtils.get(`/view/${params.tableName}?${allParamsStr}${groupByStr}${includesStr}`);
}

function configureUrlParamsForSearch({
  contextKey,
  filter = "",
  first = 0,
  size = 50,
  includeJoinParent = false,
  includeOels = [],
  includeStyle = false,
  labelContext,
  labelDetailsContext
}: FindAllParams) {
  const params = getUIContext({
    contextKey,
    labelContext: labelContext,
    labelDetailsContext: labelDetailsContext
  });

  params.append("first", first.toString());
  params.append("size", size.toString());

  if (includeJoinParent) {
    params.append("includeJoinParent", "true");
  }

  if (includeOels.length > 0) {
    params.append("includeOels", includeOels.toString());
  }

  if (includeStyle) {
    params.append("includeStyle", "true");
  }

  const allParams = [params, filter];

  return allParams
    .map(p => p.toString())
    .filter(p => p.length > 0)
    .join("&");
}
